import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faChevronDown,
  faCircleExclamation,
  faDownload,
  faEye,
  faRotate,
  faSpinner,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { useBackgroundJobs } from './BackgroundJobsContext';
import CgPopover from 'modules/Common/ui/CgPopover';
import { useFreemium } from 'modules/Common/freemium/FreemiumContext';
import { useBranding } from '../hooks/useBranding';
import { fmtElapsed } from 'modules/Common/hooks/useElapsedSeconds';
import { fetchPresignedBlob } from 'modules/OperatorReports/pdf/lambdaPresign';
import http from 'helpers/requests';
import BannerDownloadModal from './BannerDownloadModal';

const BANNER_HEIGHT = 34;

// Live elapsed (seconds) from the server `startedAt` (frozen at `finishedAt`),
// falling back to the local registration time until the first poll lands.
//
// `startedAt`/`finishedAt` may be an ISO string OR a numeric ms timestamp —
// useFullReport exposes them as "server (ISO/ms)". Use `new Date()`, which
// accepts BOTH, exactly like useElapsedSeconds (the button/center timer).
// The old `Date.parse()` returns NaN for a number, so any job whose startedAt
// arrived as ms (e.g. the full report) showed `0:00` in the banner while the
// button ticked correctly — that was the timer inconsistency.
function jobElapsedSec(job) {
  const start = job.startedAt || job.registeredAt;
  const startMs = start ? new Date(start).getTime() : NaN;
  if (!Number.isFinite(startMs) || startMs <= 0) return 0;
  const endMs = job.finishedAt ? new Date(job.finishedAt).getTime() : Date.now();
  return Math.max(0, Math.floor((endMs - startMs) / 1000));
}

function typeLabel(job, t) {
  return job.slot === 'full'
    ? t('app:jobs.typeFullReport', 'Full report')
    : t('app:jobs.typeProductionProcesses', 'Production processes');
}

// One ticking re-render per second while any job is still generating, so the
// elapsed counters stay live without each job owning its own interval.
function useSecondTick(active) {
  const [, tick] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active]);
}

function StatusLine({ job }) {
  const { t } = useTranslation('app');
  const elapsed = jobElapsedSec(job);
  if (job.status === 'pending')
    return (
      <>
        {t('app:jobs.statusGenerating', 'Generating · {{elapsed}}', {
          elapsed: fmtElapsed(elapsed),
        })}
      </>
    );
  if (job.status === 'ready')
    return (
      <>{t('app:jobs.statusReady', 'Ready · took {{elapsed}}', { elapsed: fmtElapsed(elapsed) })}</>
    );
  if (job.status === 'error')
    return (
      <>
        {t('app:jobs.statusFailed', 'Failed')}
        {job.message ? ` · ${job.message}` : ''}
      </>
    );
  return null;
}

// Pill button shown ON the colored bar (translucent white).
function BarButton({ icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 text-white text-[12px] font-medium transition-colors"
    >
      <FontAwesomeIcon icon={icon} />
      {label}
    </button>
  );
}

export default function BackgroundJobsBanner() {
  const ctx = useBackgroundJobs();
  const history = useHistory();
  const brand = useBranding();
  const { t } = useTranslation('app');
  const { hasExceededLimit } = useFreemium();
  const [open, setOpen] = useState(false);
  // Ready full-report job whose Download was clicked → opens a confirmation
  // modal that downloads the already-rendered PDF directly (no nav, no render).
  const [downloadJob, setDownloadJob] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const jobs = useMemo(() => ctx?.jobs ?? [], [ctx]);
  const visible = jobs.length > 0;

  const anyPending = jobs.some((j) => j.status === 'pending');
  const anyReady = jobs.some((j) => j.status === 'ready');

  useSecondTick(anyPending);

  // Push the rest of the app down by the bar height while it's visible (the
  // fixed Header, the report shell, and the DiagramEditor canvas all read this var
  // — the canvas auto-recomputes its height on the resize event below).
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--cg-jobs-banner-h',
      visible ? `${BANNER_HEIGHT}px` : '0px',
    );
    window.dispatchEvent(new Event('resize'));
  }, [visible]);

  // Always reset the spacer when the banner leaves the tree.
  useEffect(
    () => () => {
      document.documentElement.style.setProperty('--cg-jobs-banner-h', '0px');
      window.dispatchEvent(new Event('resize'));
    },
    [],
  );

  if (!ctx || !visible) return null;

  // Navigation: full → report page (set the marker so FullReportModal reopens
  // ready); section → report page with the section deep-link auto-selected.
  const gotoFull = (job) => {
    try {
      sessionStorage.setItem(`cg:fr-wanted:${job.reportId}`, job.inputHash);
    } catch {
      /* private mode — modal just won't auto-open */
    }
    history.push(`/diagrams/${job.diagramId}/operator-reports/${job.reportId}`);
  };
  const gotoSection = (job) =>
    history.push(
      `/diagrams/${job.diagramId}/operator-reports/${job.reportId}?section=productionProcesses&preview=1`,
    );

  const openJob = (job) => {
    setOpen(false);
    if (job.slot === 'full') {
      gotoFull(job);
    } else {
      gotoSection(job);
      // A section preview isn't downloadable — opening it IS the terminal
      // action, so clear it once the user has been taken to it.
      if (job.status === 'ready') ctx.removeJob(job.key);
    }
  };
  const retryJob = (job) => {
    setOpen(false);
    if (job.slot === 'full') gotoFull(job);
    else gotoSection(job);
  };

  // Download a ready FULL report straight from the banner: fetch the
  // already-rendered PDF from its presigned URL and save it. Never navigates to
  // the report page and never POSTs /generate. Uses the freshest copy of the job
  // (the poller may have refreshed its URL); if the cached URL is gone/expired,
  // re-asks render-status for a new one before giving up.
  const startFullDownload = async () => {
    const job = jobs.find((j) => j.key === downloadJob?.key) || downloadJob;
    if (!job) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      let blob = null;
      if (job.url) {
        try {
          blob = await fetchPresignedBlob(job.url);
        } catch {
          blob = null; // likely expired — fall through to a fresh URL
        }
      }
      if (!blob) {
        const res = await http.get(`/operator-reports/${job.reportId}/render-status`, {
          params: { slot: job.slot, inputHash: job.inputHash },
        });
        const fresh = res.data?.status === 'ready' ? res.data?.url : null;
        if (!fresh)
          throw new Error(
            t(
              'app:jobs.reportUnavailable',
              'This report is no longer available. Please regenerate it.',
            ),
          );
        blob = await fetchPresignedBlob(fresh);
      }
      const safeName = (job.name || 'report').replace(/[^\w.-]+/g, '_');
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 0);
      ctx.markDownloaded(job.key);
      setDownloadJob(null);
    } catch (e) {
      setDownloadError(
        e?.message || t('app:jobs.downloadFailed', 'Could not download the report.'),
      );
    } finally {
      setDownloading(false);
    }
  };

  const actionFor = (job) => {
    if (job.status === 'pending')
      return { icon: faEye, label: t('common:actions.view', 'View'), onClick: () => openJob(job) };
    if (job.status === 'error')
      return { icon: faRotate, label: t('app:jobs.retry', 'Retry'), onClick: () => retryJob(job) };
    return {
      icon: job.slot === 'full' ? faDownload : faEye,
      label:
        job.slot === 'full' ? t('common:actions.download', 'Download') : t('app:jobs.open', 'Open'),
      // Full → confirm + download in place; section preview → open it on the page.
      onClick: () => (job.slot === 'full' ? setDownloadJob(job) : openJob(job)),
    };
  };

  const barTone = anyPending || !anyReady ? 'primary' : 'green';
  const barClass = barTone === 'green' ? 'bg-emerald-600' : 'bg-primaryColor';
  const barIcon = anyPending ? faSpinner : anyReady ? faCheck : faCircleExclamation;

  const pendingCount = jobs.filter((j) => j.status === 'pending').length;
  const readyCount = jobs.filter((j) => j.status === 'ready').length;
  const errorCount = jobs.filter((j) => j.status === 'error').length;
  const single = jobs.length === 1;

  // Collapsed summary text for the multi-job case.
  const longestPending = jobs
    .filter((j) => j.status === 'pending')
    .reduce((max, j) => Math.max(max, jobElapsedSec(j)), 0);
  let summary;
  if (anyPending)
    summary = t('app:jobs.summaryGenerating', {
      count: pendingCount,
      elapsed: fmtElapsed(longestPending),
      defaultValue: '{{count}} report generating · {{elapsed}}',
      defaultValue_other: '{{count}} reports generating · {{elapsed}}',
    });
  else if (readyCount)
    summary = t('app:jobs.summaryReady', {
      count: readyCount,
      defaultValue: '{{count}} report ready',
      defaultValue_other: '{{count}} reports ready',
    });
  else
    summary = t('app:jobs.summaryFailed', {
      count: errorCount,
      defaultValue: '{{count}} report failed',
      defaultValue_other: '{{count}} reports failed',
    });

  // The freemium "limit reached" banner (CG-only) also pins top:0 — sit below it.
  const freemiumActive = brand?.key === 'carbonglance' && hasExceededLimit;

  const dropdown = (
    <div className="min-w-[300px] max-w-[380px] flex flex-col">
      <div className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {t('app:jobs.dropdownTitle', 'Background reports')}
      </div>
      <ul className="flex flex-col divide-y divide-slate-100">
        {jobs.map((job) => {
          const finished = job.status === 'ready' || job.status === 'error';
          const isError = job.status === 'error';
          const action = actionFor(job);
          return (
            <li
              key={job.key}
              className="flex items-start gap-2.5 py-2.5"
            >
              <span
                className={`mt-0.5 w-4 text-center text-[12px] ${
                  isError
                    ? 'text-rose-500'
                    : job.status === 'ready'
                      ? 'text-emerald-600'
                      : 'text-primaryColor'
                }`}
              >
                <FontAwesomeIcon
                  icon={
                    job.status === 'pending'
                      ? faSpinner
                      : job.status === 'ready'
                        ? faCheck
                        : faCircleExclamation
                  }
                  spin={job.status === 'pending'}
                />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-slate-800 truncate">
                  {job.name || job.subtitle || typeLabel(job, t)}
                </div>
                <div
                  className={`text-[11px] truncate ${isError ? 'text-rose-500' : 'text-slate-500'}`}
                >
                  <StatusLine job={job} />
                </div>
                {job.subtitle && job.name ? (
                  <div className="text-[10.5px] text-slate-400 truncate">{job.subtitle}</div>
                ) : null}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={action.onClick}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-[11px] font-medium"
                >
                  <FontAwesomeIcon icon={action.icon} />
                  {action.label}
                </button>
                {finished ? (
                  <button
                    type="button"
                    title={t('common:actions.dismiss', 'Dismiss')}
                    onClick={() => ctx.removeJob(job.key)}
                    className="w-6 h-6 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <>
      <div
        className={`fixed left-0 right-0 z-[1100] flex items-center gap-2 px-4 text-white ${barClass}`}
        style={{ top: freemiumActive ? 40 : 0, height: BANNER_HEIGHT }}
      >
        <FontAwesomeIcon
          icon={barIcon}
          spin={anyPending}
          className="text-[13px] shrink-0"
        />

        {single ? (
          <>
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-[13px] font-medium truncate">
                {jobs[0].name || typeLabel(jobs[0], t)}
              </span>
              <span className="text-[12px] text-white/80 truncate">
                <StatusLine job={jobs[0]} />
              </span>
            </div>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              <BarButton {...actionFor(jobs[0])} />
              {jobs[0].status !== 'pending' ? (
                <button
                  type="button"
                  title={t('common:actions.dismiss', 'Dismiss')}
                  onClick={() => ctx.removeJob(jobs[0].key)}
                  className="w-6 h-6 inline-flex items-center justify-center rounded-md text-white/80 hover:text-white hover:bg-white/15"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <CgPopover
              trigger="click"
              position="bottom"
              open={open}
              onOpenChange={setOpen}
              popoverContent={dropdown}
              popoverContentClassName="shadow-lg"
              triggerElement={
                <button
                  type="button"
                  className="inline-flex items-center gap-2 text-[13px] font-medium hover:opacity-90"
                >
                  <span className="truncate">{summary}</span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`text-[10px] transition-transform ${open ? 'rotate-180' : ''}`}
                  />
                </button>
              }
            />
            <div className="ml-auto flex items-center gap-2 shrink-0">
              {!anyPending ? (
                <button
                  type="button"
                  title={t('app:jobs.dismissAll', 'Dismiss all')}
                  onClick={() => ctx.dismissAllFinished()}
                  className="w-6 h-6 inline-flex items-center justify-center rounded-md text-white/80 hover:text-white hover:bg-white/15"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              ) : null}
            </div>
          </>
        )}
      </div>
      {downloadJob ? (
        <BannerDownloadModal
          open
          onClose={() => {
            setDownloadJob(null);
            setDownloadError(null);
          }}
          onDownload={startFullDownload}
          name={downloadJob.name}
          downloading={downloading}
          error={downloadError}
        />
      ) : null}
    </>
  );
}
